<script setup>
// BonBoZ: mục "Cần xử lý" (#232) — mỗi việc một câu dễ hiểu, ví dụ "Thu tắt
// thông báo trên iPhone lúc 02/10 16:18, đã lỡ 1.619 tin". Server đã xếp theo
// mức nặng. Loại việc lạ (server thêm sau) thì hiện tên người kèm mã.
import { useI18n, I18nT } from 'vue-i18n';
import { dateTime, usePushReportFormat } from './pushReportFormat';

defineProps({
  items: { type: Array, required: true },
});

const { t, te } = useI18n();
const { formatNumber } = usePushReportFormat();

const ICONS = {
  no_device: 'i-lucide-smartphone',
  disabled_in_app: 'i-lucide-bell-off',
  blocked: 'i-lucide-ban',
  unreachable: 'i-lucide-unplug',
  never_received: 'i-lucide-circle-help',
};

const baseKey = item =>
  `BONBOZ.PUSH_REPORT.ACTION_ITEM.${item.kind.toUpperCase()}`;
const isKnown = item => te(`${baseKey(item)}.TEXT`);

// Có mốc thời gian thì dùng câu kèm mốc; tiếng Anh có thể cần động từ khác
// cho câu đó (`ACTION_SINCE`).
const usesSince = item =>
  Boolean(item.since) && te(`${baseKey(item)}.TEXT_SINCE`);

const sentenceKey = item =>
  `${baseKey(item)}.${usesSince(item) ? 'TEXT_SINCE' : 'TEXT'}`;

const actionWord = item => {
  const sinceKey = `${baseKey(item)}.ACTION_SINCE`;
  return t(
    usesSince(item) && te(sinceKey) ? sinceKey : `${baseKey(item)}.ACTION`
  );
};

const deviceLabel = item =>
  item.device_label || t('BONBOZ.PUSH_REPORT.ACTION_ITEM.DEVICE_FALLBACK');

const missedText = item =>
  item.missed_count > 0
    ? t(
        'BONBOZ.PUSH_REPORT.ACTION_ITEM.MISSED',
        { count: formatNumber(item.missed_count) },
        item.missed_count
      )
    : '';

const itemKey = item =>
  `${item.kind}-${item.user_id}-${item.device_label || ''}-${item.since || ''}`;
</script>

<template>
  <ul class="flex flex-col gap-2 p-0 m-0 list-none">
    <li
      v-for="item in items"
      :key="itemKey(item)"
      class="flex items-start gap-2 text-sm text-n-slate-12"
      data-testid="push-action-item"
    >
      <span
        class="mt-0.5 size-4 shrink-0 text-n-ruby-11"
        :class="ICONS[item.kind] || 'i-lucide-triangle-alert'"
        aria-hidden="true"
      />
      <I18nT
        v-if="isKnown(item)"
        :keypath="sentenceKey(item)"
        tag="span"
        scope="global"
        data-testid="push-action-sentence"
      >
        <template #user>{{ item.user_name }}</template>
        <template #action>
          <strong class="font-semibold">{{ actionWord(item) }}</strong>
        </template>
        <template #device>{{ deviceLabel(item) }}</template>
        <template #since>{{ dateTime(item.since) }}</template>
        <template #missed>{{ missedText(item) }}</template>
      </I18nT>
      <span v-else data-testid="push-action-sentence">
        <strong class="font-medium">{{ item.user_name }}</strong>
        {{ `· ${item.kind}` }}
      </span>
    </li>
  </ul>
</template>
